import Vue from "vue";
import Vuex from "vuex";
Vue.use(Vuex);
import VueCookies from 'vue-cookies'
Vue.use(VueCookies)

import lodash from 'lodash';
import deepdash from 'deepdash';
const _ = deepdash(lodash);

import { commonStore } from "./commonStore";
import { authorizationStore } from "./authorizationStore";
import { classificationStore } from "./classificationStore";
import { store as canvasDesignStore } from "./canvasDesignStore";
import commons from "../commons/commons";
import Axios from "axios";
import webAppConfig from "../commons/webApp.config";
import { buildDocumentRequest, HTML_DOCUMENT_PATH, DOCUMENT_POST_CONTENT_TYPE } from "../commons/documentRequest";
import LocalStorageService from "./LocalStorageService";
import { xCall, getWorkspaceBootstrap } from "./xClient";
import { buildXListMenuMap, updateXListModuleMap, updateXListListingMap } from "./xListGate";

function isRunAsOrDemoRequest(state) {
  const wsId = state &&
    state.client &&
    state.client.defaults &&
    state.client.defaults.headers &&
    state.client.defaults.headers.wsId;

  return Boolean(
    typeof wsId === "string" &&
    (
      wsId.indexOf("runas:") === 0 ||
      wsId.indexOf("demo:") === 0
    )
  );
}

function shouldUseXListEndpoint(state, payload) {
  if (!payload) return false;
  if (isRunAsOrDemoRequest(state)) return false;
  if (payload.startFieldName) return false;

  return Boolean(
    state.xListMenus &&
    state.xListMenus[payload.moduleId] &&
    state.xListMenus[payload.moduleId][payload.listingId]
  );
}


// kept outside Vuex state: an AbortController is a native object with internal
// listeners/getters, and Vue 2's reactivity would recursively walk/instrument it
// on every assignment if it lived in reactive state.
let workspaceAbortController = null;

const getInitialState = () => ({
  ...commonStore.state,
  workspace: {},
  workspaceModules: [],
  xListMenus: {},
  workspaceContext: null,
  isLiteMode: null,
  availableModules: [],
  module: {},
  moduleImport: {},
  list: {},
  basket: [],
  designMode: false,
  designModeLoading: false,
  designModeState: null,
  liteDesignMode: false,
});

export const clientStore = new Vuex.Store({
  state: getInitialState(),
  mutations: {
    ...commonStore.mutations,
    resetState(state) {
      Object.assign(state, getInitialState());
    },
    designMode(state, data) {
      state.designMode = data;
      if (!data) {
        state.designModeState = null;
      }
    },
    designModeLoading(state, data) {
      state.designModeLoading = data;
    },
    designModeState(state, data) {
      state.designModeState = data;
    },
    setLiteDesignMode(state, data) {
      state.liteDesignMode = data;
    },
    setWorkspace(state, data) {
      if (
        !Boolean(window.router) ||
        (
          window.router &&
          window.router.currentRoute &&
          window.router.currentRoute.name !== 'shareForm'
        )
      ) {
        $cookies.set('wsId', data.id);
        LocalStorageService.setWorkspaceId(webAppConfig.baseServer, data.id);

        if (!authorizationStore.getters.isRunAs) {
          LocalStorageService.setWorkspace(webAppConfig.baseServer, data);
        }
      }
      state.client.defaults.headers['wsId'] = data.id;
      if (data.demoId) {
        state.client.defaults.headers['wsId'] = `demo:${data.id}:${data.demoId}`;
      } else if (authorizationStore.getters.isRunAs) {
        state.client.defaults.headers['wsId'] = authorizationStore.state.runAs.wsId;
      }

      state.workspace = data;
      state.workspaceModules = data.vectors;
      state.isLiteMode = Boolean(data.type && data.type.toLowerCase() === 'lite');
      state.xListMenus = buildXListMenuMap(data);

      if (!state.workspace.hasContext) {
        state.workspaceContext = null;
      }
      /* set workspace name */
      classificationStore.dispatch("setWorkspace", data.name);
      /* set users */
      classificationStore.dispatch('setUsers', { users: data.users });
      canvasDesignStore.dispatch('setUsers', data.users);
    },
    /* Name a workspace on every request from here on, before its bootstrap has loaded: the moment
       one has just been created. The server reads the workspace from the wsId header. Without one
       it falls back to the caller's only workspace, then to the cookie, which a cross-origin API
       never receives; and a header left naming another workspace wins over the cookie. So the
       cookie alone answered a person with another workspace for the wrong one, or none (SS06115). */
    setWorkspaceId(state, id) {
      $cookies.set('wsId', id);
      LocalStorageService.setWorkspaceId(webAppConfig.baseServer, id);
      state.client.defaults.headers['wsId'] = id;
    },
    /* Portal links: Settings › Portals hands the admin's own side menu the list it just changed. A
       portal change does not move the workspace version, so the next bootstrap may answer "not
       modified" and serve the cached copy: that copy is updated too. */
    setWorkspacePortals(state, portals) {
      if (!state.workspace || !state.workspace.id) return;
      state.workspace = { ...state.workspace, portals: Array.isArray(portals) ? portals : [] };
      if (!authorizationStore.getters.isRunAs && !state.workspace.demoId) {
        try {
          LocalStorageService.setWorkspace(webAppConfig.baseServer, state.workspace);
        } catch (err) {
          /* a full or blocked storage only costs the cached copy */
        }
      }
    },
    setModule(state, data) {
      state.module = data;
    },
    syncWorkspaceModule(state, data) {
      if (state.module && state.module.id == data.id) {
        state.module = {
          ...state.module,
          ...data
        };
      }

      if (state.workspace && Array.isArray(state.workspace.vectors)) {
        const workspaceModule = state.workspace.vectors.find(x => x.id == data.id);

        if (workspaceModule) {
          Object.assign(workspaceModule, data);
        }
      }

      if (state.workspace && Array.isArray(state.workspace.menus)) {
        const workspaceMenu = _.findDeep(
          state.workspace.menus,
          x => x && x.id == data.id,
          { childrenPath: 'children' }
        );

        if (workspaceMenu && workspaceMenu.value) {
          Object.assign(workspaceMenu.value, data);
        }
      }

      /* Today's only caller patches a scalar field, so rebuilding from the module's existing
         menus is a no-op -- but the gate is derived from flags this mutation is free to
         carry, and a caller that ever patches `menus` must not leave the map behind
         (73159efb put this here for that reason). */
      if (state.workspace && Array.isArray(state.workspace.vectors)) {
        const patchedModule = state.workspace.vectors.find(x => x.id == data.id);

        if (patchedModule) {
          updateXListModuleMap(state.xListMenus, patchedModule);
        }
      }
    },
    syncModuleListing(state, { moduleId, listingId, data }) {
      if (!moduleId || !listingId || !data) return;

      if (state.list && state.list.id == listingId) {
        state.list = {
          ...state.list,
          ...data
        };
      }

      const updateListing = (listings) => {
        if (!Array.isArray(listings)) return null;

        const listing = listings.find(x => x && x.id == listingId);
        if (!listing) return null;

        Object.assign(listing, data);
        return listing;
      };

      let moduleListing = null;
      let workspaceListing = null;

      if (state.module && state.module.id == moduleId) {
        moduleListing = updateListing(state.module.menus);
      }

      if (state.workspace && Array.isArray(state.workspace.vectors)) {
        const workspaceModule = state.workspace.vectors.find(x => x.id == moduleId);
        if (workspaceModule) {
          workspaceListing = updateListing(workspaceModule.menus);
        }
      }

      if (state.workspace && Array.isArray(state.workspace.menus)) {
        const workspaceModuleMenu = _.findDeep(
          state.workspace.menus,
          x => x && x.id == moduleId,
          { childrenPath: 'children' }
        );

        if (
          workspaceModuleMenu &&
          workspaceModuleMenu.value &&
          Array.isArray(workspaceModuleMenu.value.children)
        ) {
          updateListing(workspaceModuleMenu.value.children);
        }
      }

      /* The /x gate is derived from these very flags, so it has to follow them. A listing
         that just gained a saved column preference must leave the lane at once: /x/list
         refuses it, and a port that predates that refusal answers with the DEFAULT columns
         -- silently undoing the preference the user just saved (SS06055). The bootstrap
         already withholds x for such a listing, but the copy in memory was fetched before
         the save. Prefer the workspace vector's listing, the same object the map was
         built from. */
      const gateListing = workspaceListing || moduleListing;
      if (gateListing) {
        updateXListListingMap(state.xListMenus, moduleId, gateListing);
      }
    },
    setList(state, data) {
      state.list = data;
    },
    setWorkspaceName(state, data) {
      state.workspace.name = data;
      LocalStorageService.setWorkspace(webAppConfig.baseServer, state.workspace);
    },
    setWorkspaceUsers(state, data) {
      state.workspace.users = data;
      // set users
      classificationStore.dispatch("setUsers", { users: data });
      canvasDesignStore.dispatch("setUsers", data);
    },
    setWorkspaceMenus(state, data) {
      state.workspace.menus = data;
    },
    setWorkspaceContext(state, data) {
      $cookies.set('wsContext', data);
      state.workspaceContext = data;
    },
    setWorkspaceWidget(state, data) {
      state.workspace = data;
      LocalStorageService.setWorkspace(webAppConfig.baseServer, data);
    },
    setWorkspaceStates(state, data) {
      state.workspace.states = data;
    }
  },
  actions: {
    ...commonStore.actions,
    resetState({ commit }) {
      commit('resetState');
    },
    setDesignMode({ commit, getters }, { mode, skipLog }) {
      commit('designMode', Boolean(mode));
      if (!skipLog && getters.moduleId) {
        LocalStorageService.setModuleListingPreference(
          getters.moduleId,
          getters.listId,
          {
            ...LocalStorageService.getModuleListingPreference(getters.moduleId, getters.listId),
            designMode: Boolean(mode)
          }
        )
      }
    },
    setDesignModeLoading({ commit }, val) {
      commit('designModeLoading', Boolean(val));
    },
    setDesignModeState({ commit }, val) {
      commit('designModeState', val);
    },
    syncWorkspaceModule({ commit }, payload) {
      commit('syncWorkspaceModule', payload);
    },
    syncModuleListing({ commit }, payload) {
      commit('syncModuleListing', payload);
    },
    setLiteDesignMode({ commit }, val) {
      commit('setLiteDesignMode', val);
    },
    setWorkspaceModuleStates({ commit, getters }, payload) {
      const states = payload.states.map((x) => ({...x, module: payload.moduleId.toString()}));
      if (Array.isArray(getters.workspaceStates)) {
        commit('setWorkspaceStates', 
          [
            ...getters.workspaceStates.filter((x) => x.module != payload.moduleId),
            ...states
          ]
        );
      } else {
        commit('setWorkspaceStates', states);
      }
    },
    setWorkspace({ state, commit }, payload = {}) {
      try {
        let workspace = payload.workspace;
        let cloneWorkspaceMenus = null;

        authorizationStore.dispatch('setRoles', workspace.roles);

        if (authorizationStore.getters.isAdminUser) {
          const menuEverything = { id: "everything", name: "Everything", emoji: "🗃️" };
          workspace.vectors = workspace.vectors.map(x => {
            if (x.menus.length) {
              let findMenuEverything = x.menus.find(y => y.id == "everything");
              if (findMenuEverything) {
                findMenuEverything = {...findMenuEverything, ...menuEverything};
              } else {
                return {
                  ...x,
                  menus: [...x.menus, {...menuEverything}]
                }
              }
            }

            return x;
          });
        }

        commons.expandMenu(workspace.menus, workspace.vectors);
        
        if (payload.preserveMenuState)
          cloneWorkspaceMenus = _.cloneDeep(state.workspace.menus);

        if (payload.demoId)
          workspace.demoId = payload.demoId;

        commit('setWorkspace', workspace);

        if (workspace && workspace.hasContext) {
          const context = $cookies.get('wsContext');
          context && commit('setWorkspaceContext', context);
        }

        if (payload.preserveMenuState) {
          if (cloneWorkspaceMenus && Array.isArray(cloneWorkspaceMenus))
            commons.setOpenedMenus(state.workspace.menus, cloneWorkspaceMenus);

          if ((state.list && state.list.parentId) || state.module && state.module.id)
            commons.expandSidebarMenu(
              state.module.id ? state.module.id : state.list.parentId,
              state.workspace.menus
            );
        }

        const flattenWorkspaceMenuModules = (menus) => {
          return menus.flatMap(menu => [
            ...(menu.module !== undefined ? [menu.module] : []),
            ...(menu.children ? flattenWorkspaceMenuModules(menu.children) : [])
          ]);
        }

        state.availableModules = flattenWorkspaceMenuModules(state.workspace.menus);
      } catch(err) {
        throw err;
      } 
    },
    async loadWorkspaceShareForm({ state, commit }, payload) {
      if (workspaceAbortController) {
        workspaceAbortController.abort();
      }
      const abortController = new AbortController();
      workspaceAbortController = abortController;

      try {
        let route = payload.route;
        let res = await Axios.get(`${webAppConfig.baseURL}/api/workspace/0`, {
          headers: {
            Authorization: `inishare ${payload.route.params.id}:${payload.route.query.code}`
          },
          signal: abortController.signal
        })
        commit('setWorkspace', res.data)
        return { stale: false };
      } catch (err) {
        if (Axios.isCancel(err) || err.code === 'ERR_CANCELED') {
          return { stale: true };
        }
        throw err
      }
    },
    async loadWorkspace({ state, dispatch }, payload = {}) {
      if (workspaceAbortController) {
        workspaceAbortController.abort();
      }
      const abortController = new AbortController();
      workspaceAbortController = abortController;

      try {
        let workspaceId = payload.id || $cookies.get('wsId');
        if (!workspaceId || payload.setupMode) {
          const res = await dispatch('getWorkspaces', { search: '' });
          if (Array.isArray(res) && res[0] && res[0].id) {
            workspaceId = res[0].id;
          } else {
            if (payload.setupMode) {
              return;
            }
            throw {
              status: 404,
              error: 'No workspace found. Please contact your administrator.'
            };
          }
        }

        let url = `${workspaceId}`;
        let lsWorkspaceData = {};

        if (!payload.forceReload) {
          lsWorkspaceData = LocalStorageService.getWorkspace(webAppConfig.baseServer, workspaceId);
          if (lsWorkspaceData && lsWorkspaceData.id && lsWorkspaceData.version && (lsWorkspaceData.id == workspaceId)) {
            url += `?version=${lsWorkspaceData.version}`;
          }
        }

        const res = await getWorkspaceBootstrap(state.client, url, { signal: abortController.signal });
        const workspace = res.data && res.data.id ? res.data : lsWorkspaceData;

        dispatch('setWorkspace', { ...payload, workspace: workspace });
        return { stale: false };
      } catch (err) {
        if (Axios.isCancel(err) || err.code === 'ERR_CANCELED') {
          return { stale: true };
        }

        console.error(err);

        /* SS06178/SS06180: a background refresh (hourly check, tab return, a workspace/user change
           pushed over SignalR) that fails - e.g. an intermittent 500 from /x/workspace - must not
           forget the workspace on screen. Clearing it here made the caller's retry fall back to
           the FIRST workspace in the list, so the user was switched away without doing anything. */
        if (payload.background) {
          throw err;
        }

        if ($cookies.get('wsId')) {
          $cookies.remove('wsId');
        }

        if (LocalStorageService.getWorkspaceId(webAppConfig.baseServer)) {
          LocalStorageService.removeWorkspaceId(webAppConfig.baseServer);
        }

        LocalStorageService.clearWorkspace(webAppConfig.baseServer);
        throw err;
      }
    },
    async loadWorkspaceByModuleId({ state, dispatch }, payload = {}) {
      if (workspaceAbortController) {
        workspaceAbortController.abort();
      }
      const abortController = new AbortController();
      workspaceAbortController = abortController;

      try {
        const moduleId = payload.moduleId;
        if (!moduleId) {
          throw {
            status: 404,
            error: 'No workspace found. Please contact your administrator.'
          };
        }

        const res = await getWorkspaceBootstrap(state.client, `0?vector=${moduleId}`,
          { signal: abortController.signal });
        const workspace = res.data && res.data.id ? res.data : null;

        if (!workspace) {
          throw {
            status: 404,
            error: 'No workspace found. Please contact your administrator.'
          };
        }
        dispatch('setWorkspace', { ...payload, workspace: workspace });
        return { stale: false };
      } catch(error) {
        if (Axios.isCancel(error) || error.code === 'ERR_CANCELED') {
          return { stale: true };
        }

        console.error(error);

        if ($cookies.get('wsId')) {
          $cookies.remove('wsId');
        }

        if (LocalStorageService.getWorkspaceId(webAppConfig.baseServer)) {
          LocalStorageService.removeWorkspaceId(webAppConfig.baseServer);
        }

        LocalStorageService.clearWorkspace(webAppConfig.baseServer);
        throw error;
      }
    },
    async loadWorkspaceUsers({ state }, payload) {
      try {
        const res = await getWorkspaceBootstrap(state.client, `${payload.id}`);
        return res.data.users;
      } catch(err) {
        throw err;
      }
    },
    async loadDiagram({ state }, payload) {
      return await state.client.get(`/api/workspace/diagram/${commons.decodeids(payload)}`);
    },
    async trace({ state }, payload) {
      return await state.client.get(`/api/activity/trace/${payload}`);
    },
    async updateLobby({ state }, payload) {
      return await state.client.post(`/api/user/lobby`, payload);
    },
    async acceptRejectUser({ state }, payload) {
      return await state.client.post(`/api/user/lobbyApproval`, payload);
    },
    async loadDocumentInputs({ state }, payload) { /* never read */
      return await state.client.get(`/api/activity/documentinputs?moduleid=${payload.moduleId}&templateid=${payload.tempalteId}`);
    },
    setWorkspaceName({ commit }, payload) {
      commit('setWorkspaceName', payload);
    },
    setWorkspaceMenus({ commit }, payload) {
      commit('setWorkspaceMenus', payload);
    },
    /* dashboard widget */
    async setWorkspaceWidget({ state, commit }, payload) {
      let workspace = { ...state.workspace };
      if (payload.hasOwnProperty('shortcuts')) {
        workspace.shortcuts = payload.shortcuts;
      }
      if (payload.hasOwnProperty('widgets')) {
        workspace.widgets = payload.widgets;
      }
      if (payload.hasOwnProperty('dashboard')) {
        workspace.dashboard = payload.dashboard;
      }
      commit('setWorkspaceWidget', workspace);
    },
    async updateDashboardWidget({ state, getters }, payload) {
      const shortcuts = payload && payload.shortcuts ? payload.shortcuts : _.cloneDeep(getters.workspaceShortcuts);
      const widgets = payload && payload.widgets ? payload.widgets : _.cloneDeep(getters.workspaceWidgets);
      const dashboard = payload && payload.dashboard ? payload.dashboard : _.cloneDeep(getters.workspaceDashboard);
      return await state.client.post('/api/workspace/updatedashboard', {
        id: getters.workspaceId,
        shortcuts,
        widgets,
        dashboard
      });
    },
    //export
    async exportData({ state, commit, dispatch }, payload) {
        return await state.client.post('/api/document/export', payload, { responseType: 'blob' });
    },
    //export request
    async exportRequest({ state, commit, dispatch }, payload) {
        return await state.client.post('/api/document/exportRequest', payload);
    },
    //from listview, todo: to be refactor
    async loadWorkspaceModule({ state, commit, dispatch }, payload) {
      const moduleId = commons.decodeids(payload.moduleId)
      try {
        /* workspace */
        if (_.isEmpty(state.workspace) || !state.workspaceModules.find(x => x.id == moduleId)) {
          if (workspaceAbortController) {
            workspaceAbortController.abort();
          }
          const abortController = new AbortController();
          workspaceAbortController = abortController;

          let res;
          try {
            res = await state.client.get(`/api/workspace/0?vector=${moduleId}`, { signal: abortController.signal });
          } catch (err) {
            if (Axios.isCancel(err) || err.code === 'ERR_CANCELED') {
              return { stale: true };
            }
            throw err;
          }

          authorizationStore.dispatch('setRoles', res.data.roles);
          commons.expandMenu(res.data.menus, res.data.vectors)
          dispatch('setWorkspace', { workspace: res.data });
        }
        /* module */
        let module = state.workspaceModules.find(x => x.id == moduleId)
        if (module)
          commit('setModule', module)
        /* list */
        if (payload.listId) {
          const list = _.findDeep(state.workspace.menus, (value, key, parentValue, context) => 
            commons.isObject(parentValue) && parentValue.id == moduleId && value.id == payload.listId, 
          { childrenPath: 'children'});
          if (list && list.value && list.parent && list.parent.id && list.parent.type != 'group') {
            commit('setList', {
              id: list.value.id,
              name: list.value.name,
              emoji: list.value.emoji,
              parentId: list.parent.id
            })
          } else {
            commit('setList', {})
          }
        } else {
          commit('setList', {})
        }
        /* expand sidebar menu */
        if ((state.list && state.list.parentId) || state.module && state.module.id) {
          let workspaceMenus = _.cloneDeep(state.workspace.menus)
          commons.expandSidebarMenu(
            state.module.id ? state.module.id : state.list.parentId,
            workspaceMenus
          )
          state.workspace.menus = workspaceMenus;
        }
      } catch (err) {
        throw err;
      }
      return { stale: false };
    },
    resetModule({ commit }) {
      commit('setModule', {});
      commit('setList', {});
    },
    // Guided onboarding P1 (docs/frontend-guided-onboarding-proposal.md). The sign-up questionnaire's
    // answers: the person's half lands on their profile, the workspace's half on the workspace, and
    // both merge — so the two slides save independently and a Skip records itself.
    async getOnboarding({ state }) {
      const res = await state.client.get('/api/workspace/onboarding');
      return res.data || {};
    },
    async saveOnboarding({ state }, payload) {
      const res = await state.client.put('/api/workspace/onboarding', payload || {});
      return res.data || {};
    },
    // P2: the starter workflows, ranked by the server against the answers; installing one; saying
    // "later"; and clearing the sample records it came with.
    async getOnboardingStarters({ state }) {
      const res = await state.client.get('/api/workspace/onboarding/starters');
      return res.data || {};
    },
    async buildFirstWorkflow({ state }, payload) {
      const res = await state.client.post('/api/workspace/onboarding/build', payload || {});
      return res.data || {};
    },
    /** Describe your own (guided onboarding P3): GNey designs and builds it; progress is on getOnboarding. */
    async designFirstWorkflow({ state }, payload) {
      const res = await state.client.post('/api/workspace/onboarding/design', payload || {});
      return res.data || {};
    },
    async dismissFirstWorkflow({ state }) {
      const res = await state.client.post('/api/workspace/onboarding/dismiss', {});
      return res.data || {};
    },
    async clearOnboardingSamples({ state }) {
      const res = await state.client.delete('/api/workspace/onboarding/samples');
      return res.data || {};
    },
    async getWorkspaces({ state }, payload) {
      const search = payload && payload.search ? encodeURIComponent(payload.search) : "";
      try {
        let res = await state.client.get(`/api/workspace?search=${search}`)
        return res.data || []
      } catch (err) {
        console.error(err)
        return []
      }
    },
    async getFirstOrDefaultWorkspaceId({ dispatch }) {
      const res = await dispatch('getWorkspaces', { search: '' });
      return res && res[0] && res[0].id;
    },
    async loadDocumentForm({ state }, payload) {
      return await state.client.post('/api/activity/form', payload);
    },
    async sendInvitation({ state }, payload) {
      return await state.client.post('/api/workspace/invites', payload);
    },
    async getInvitationLink({ state }, payload) {
      try {
        let res = await state.client.post('/api/workspace/invites', payload);
        return res.data
      } catch (err) {
        throw err
      }
    },
    setWorkspaceId({ commit }, id) {
      commit('setWorkspaceId', id);
    },
    /* workspace context */
    setWorkspaceContext({ commit }, payload) {
      commit('setWorkspaceContext', payload)
    },
    async getWorkspaceContexts({ state, getters }, payload) {
      try {
        let res = await state.client.get(`/api/workspace/context?id=${getters.workspaceId}&search=${payload.search}`)
        return res.data || []
      } catch (err) {
        console.error(err)
        return []
      }
    },
    /* list view */
    async loadWorkspaceList({ state }, payload) {
      const useXList = shouldUseXListEndpoint(state, payload);

      const commitXFormMap = (res) => {
        if (res.data.header) {
          classificationStore.dispatch('setXFormMap', {
            listingId: payload.listingId,
            moduleId: payload.moduleId,
            compatibleStandards: res.data.header.compatibleStandards,
            activities: res.data.header.activities
          });
        }
      };

      const res = await xCall(state.client, 'list', { payload, gate: useXList });
      commitXFormMap(res);
      return res;
    },
    /* dashboard */
    async retrieveDashboard({ state }, payload) {
      return await state.client.get(`/api/workspace/dashboard?activityeveryone=${payload.activityEveryone}`);
    },
    /* SS03547: the workspace's "everyone" access policy as it applies to this caller -
       { restrictActivityStream, restrictSharedCalendar, activityEveryoneAllowed, sharedCalendarAllowed }.
       The dashboard gets activityEveryoneAllowed on its own payload; the calendar form asks here. */
    async retrieveEveryoneAccess({ state }, workspaceId) {
      return await state.client.get(`/api/workspace/EveryoneAccess?workspaceId=${workspaceId}`);
    },
    /* widget */
    async previewWidget({ state }, payload) {
      return await state.client.post(`/api/workspace/previewWidget/`, payload);
    },
    /* widget */
    async retrieveWidget({ state }, payload) {
      if (
        window.router &&
        window.router.currentRoute &&
        window.router.currentRoute.meta &&
        window.router.currentRoute.meta.external
      ) {
        const query= window.router.currentRoute.query;
        let wsId = query.id;
        if(query.runas){
          wsId = `runas:${query.id}:${query.runas}`;
        }

        return await Axios.post(`${webAppConfig.baseURL}/api/workspace/widget/${payload.itemId}`, payload.filters, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${query.token}`,
            "tz": Intl.DateTimeFormat().resolvedOptions().timeZone,
            "wsId": wsId
          }
        });
      } else {
        return await state.client.post(`/api/workspace/widget/${payload.itemId}`, payload.filters);
      }
    },
    /* listing card design (AppList Designer v2, E2): what the card is, three GNey candidates, apply */
    async loadListingCardDesign({ state }, { moduleId, listingId }) {
      const res = await state.client.get(`/api/configure/${encodeURIComponent(moduleId)}/listing/${encodeURIComponent(listingId)}/appList`);
      return res.data;
    },
    async suggestListingCardDesign({ state }, { moduleId, listingId, hint, count, layout }) {
      const res = await state.client.post(
        `/api/configure/${encodeURIComponent(moduleId)}/listing/${encodeURIComponent(listingId)}/appList/suggest`,
        { count: count || 3, hint: hint || null, layout: layout || null });
      return res.data;
    },
    async applyListingCardDesign({ state }, { moduleId, listingId, appList, origin }) {
      const res = await state.client.put(
        `/api/configure/${encodeURIComponent(moduleId)}/listing/${encodeURIComponent(listingId)}/appList`,
        { appList, origin });
      return res.data;
    },
    /* notification */
    async retrieveNotification({ state }, payload) {
      return await state.client.post('/api/workspace/notifications', payload);
    },
    async retrieveNotificationCount({ state }) {
      return await state.client.get('/api/workspace/notificationcount')
    },
    /** One notification in full (content body, source, subject entry); marks it read server-side. */
    async retrieveNotificationDetail({ state }, id) {
      return await state.client.get(`/api/workspace/notification/${encodeURIComponent(id)}`);
    },
    async clearNotificationUnreceivedCount({ state }) {
      return await state.client.get('/api/workspace/clearnotificationunreceived')
    },
    /* htmlTemplate */
    async htmlTemplateProcess({ state }, payload) {
      // SS06238: GET as before; POST the model when the query string would be too long for IIS
      const request = buildDocumentRequest(HTML_DOCUMENT_PATH, payload);
      if (request.method === "POST") {
        return await state.client.post(request.url, request.body, {
          headers: { "Content-Type": DOCUMENT_POST_CONTENT_TYPE },
        });
      }
      return await state.client.get(request.url)
    },
    /* htmljs document viewer (end-user path — separate from the legacy PdfSharp/HTML documents).
       docData  = typed DocumentDataModel (auth-gated on documentId); docBundleGet = the stored
       {bundle,page} for live client-side preview; docRender = the byte-exact server PDF (Download).
       /doc/* is served identically by legacy and net10; the load balancer picks the tier. */
    async docData({ state }, payload) {
      return await state.client.post('/doc/data', payload);
    },
    async docBundleGet({ state }, ref) {
      return await state.client.get('/doc/bundle/' + ref);
    },
    async docRender({ state }, payload) {
      return await state.client.post('/doc/render', payload, { responseType: 'blob' });
    },
    /* The viewer-facing manifest of one document ({ engine, templateRef, page, ... }) for a caller
       that holds only its id - a document link field (documentLink.js). */
    async docManifest({ state }, { moduleId, documentId }) {
      return await state.client.get('/doc/manifest', { params: { moduleId, documentId } });
    },
    /* my calendar */
    async getMyCalender({ state }, payload) {
      return await state.client.post('/api/workspace/calendar', payload)
    },
    async setMyCalendarEvent({ state }, payload) {
      return await state.client.post('/api/workspace/addtomycalendar', payload)
    },
    /* solution */
    async loadWorkspaceSolutions({ state }, payload) {
      return Boolean(payload.search) ?
        await state.client.get(`/api/workspace/solutions?search=${encodeURI(payload.search)}`) :
        await state.client.get('/api/workspace/solutions');
    },
    async loadAISolution({ state }, payload) {
      return await state.client.get(`/api/ai?type=system&text=${encodeURI(payload.search)}&count=${payload.count}`);
    },
    /* user */
    setWorkspaceUser({ getters, commit }, payload) {
      let workspaceUsers = [...getters.workspaceUsers];
      const userIndex = workspaceUsers.findIndex(user => user.username == payload.username);
      if (userIndex !== -1) {
        workspaceUsers[userIndex] = payload;
        commit("setWorkspaceUsers", workspaceUsers);
      }
    },
    removeWorkspaceUser({ getters, commit }, payload) {
      let workspaceUsers = [...getters.workspaceUsers];
      const userIndex = workspaceUsers.findIndex(user => user.username == payload);
      if (userIndex !== -1) {
        workspaceUsers.splice(userIndex, 1);
        commit("setWorkspaceUsers", workspaceUsers);
      }
    },
    /* member */
    async getMember({ state }, payload) {
      return await state.client.get(`/api/user/list?search=${encodeURI(payload.searchDisplayName)}&type=${encodeURI(payload.type)}`)
    },
    async loadLobby({ state }, payload) {
      return await state.client.get(`/api/user/lobby`)
    },
    async updateMember({ state }, payload) {
      return await state.client.post('/api/user/updatedetail', payload)
    },
    async resetUserPassword({ state }, payload) {
      return await state.client.post('/api/user/resetpassword/', payload)
    },
    async setDisableEnable({ state }, payload) {
      return await state.client.post('/api/user/disableenable', payload)
    },
    async unlockUser({ state }, payload) {
      return await state.client.post('/api/user/unlock', payload)
    },
    async setRole({ state }, payload) {
      return await state.client.post('/api/user/setRole', payload)
    },
    // Membership tier — "Pro" is the actor seat (pricing spec §16), the only seat that can
    // connect externally; backend restricts this to internal staff and enforces the plan's
    // add-on slot cap (409 slots_exhausted).
    async setMemberType({ state }, payload) {
      return await state.client.post('/api/user/setMemberType', payload)
    },
    // Whether the caller may give or take actor seats here (not on a vendor-billed workspace).
    async getActorSeatAccess({ state }) {
      return await state.client.get('/api/user/actorSeatAccess')
    },
    // Consultant seniority grant (spec §16 R23) — internal staff only.
    async setConsultantSeniority({ state }, payload) {
      return await state.client.post('/api/user/setConsultantSeniority', payload)
    },
    // Per-member GNey switch (pricing spec §11 R3) — a workspace admin's call; the server checks
    // it on this workspace's membership.
    async setGney({ state }, payload) {
      return await state.client.post('/api/user/setGney', payload)
    },
    async loadHistory({ state }, payload) {
      return await state.client.get(`/api/activity/historyitem/${payload.type},${payload.id}`);
    },
    /* API connections (personal access tokens) */
    // Capability probe: feature flag + whether this user's seat tier
    // qualifies under the pricing gate. Drives the sidenav entry point.
    async getConnectionsAvailability({ state }) {
      return await state.client.get('/api/connections/availability');
    },
    async getApiConnections({ state }) {
      return await state.client.get('/api/connections');
    },
    async getApiConnectionWorkspaces({ state }, payload) {
      // The same workspace listing the MCP server uses; scoped to the signed-in user. It answers
      // 10 workspaces a page, filtered by name (SS06304).
      const { search = '', page = 0 } = payload || {};
      return await state.client.get('/api/workspace', { params: { search, page } });
    },
    async createApiConnection({ state }, payload) {
      return await state.client.post('/api/connections', payload);
    },
    async updateApiConnection({ state }, payload) {
      return await state.client.put(`/api/connections/${payload.id}`, payload.payload);
    },
    async rotateApiConnection({ state }, id) {
      return await state.client.post(`/api/connections/${id}/rotate`);
    },
    async revokeApiConnection({ state }, id) {
      return await state.client.post(`/api/connections/${id}/revoke`);
    },
    /* Module Store apps (SS05963) — static app modules GNey acts through.
       Config is write-only for secrets: the token goes up, never comes back. */
    async getAppsAvailability({ state }) {
      return await state.client.get('/api/apps/availability');
    },
    async getApps({ state }) {
      return await state.client.get('/api/apps');
    },
    async getAppStatus({ state }, appId) {
      return await state.client.get(`/api/apps/${appId}/status`);
    },
    async installApp({ state }, appId) {
      return await state.client.post(`/api/apps/${appId}/install`);
    },
    async configureApp({ state }, payload) {
      return await state.client.post(`/api/apps/${payload.appId}/config`, payload.config);
    },
    async testApp({ state }, appId) {
      return await state.client.post(`/api/apps/${appId}/test`);
    },
    /* Revokes the stored credentials; the module and its history stay. Apps whose credentials are
       shared with a workspace channel answer needs_confirmation until confirmed is passed. */
    async disconnectApp({ state }, payload) {
      return await state.client.post(`/api/apps/${payload.appId}/disconnect`, {
        confirmed: payload.confirmed === true,
      });
    },
    /* Real end-to-end send: the admin picks the destination, the server owns the wording. */
    async sendAppTestMessage({ state }, payload) {
      return await state.client.post(`/api/apps/${payload.appId}/test-message`, {
        to: payload.to || null,
      });
    }
  },
  getters: {
    workspaceId: state => state.workspace && state.workspace.id,
    workspaceName: state => state.workspace && state.workspace.name,
    workspaceBeta: state => !authorizationStore.getters.demoMode && Boolean(state.workspace && state.workspace.beta),
    workspaceModules: state => state.workspace && state.workspace.vectors,
    workspaceStates: state => state.workspace && state.workspace.states,
    workspaceUsers: state => state.workspace && state.workspace.users,
    workspaceShortcuts: state => state.workspace && state.workspace.shortcuts,
    workspaceWidgets: state => state.workspace && state.workspace.widgets,
    workspaceDashboard: state => state.workspace && state.workspace.dashboard,
    workspaceMenus: state => state.workspace && state.workspace.menus,
    workspaceCountry: state => state.workspace && state.workspace.country,
    workspaceBuilder: state => Boolean(state.workspace && state.workspace.builder),
    workspaceLobby: state => Boolean(state.workspace && state.workspace.inLobby),
    availableModules: state => Array.isArray(state.availableModules) ? state.availableModules : [],
    companyName: state => state.workspace && state.workspace.company && state.workspace.company.name,
    companyLogo: state => state.workspace && state.workspace.company && state.workspace.company.logo,
    module: state => state.module,
    moduleId: state => state.module && state.module.id,
    moduleStates: (state, getters) =>
      getters.moduleId && getters.workspaceStates && getters.workspaceStates.filter(
        x => x.module == getters.moduleId
      )
    ,
    list: state => state.list,
    listId: state => state.list && state.list.id,
        designMode: state => state.designMode,
    designModeLoading: state => state.designModeLoading,
    designModeState: state => state.designModeState,
    liteDesignMode: state => state.liteDesignMode
  }
});